import type { EventData, venueType } from "../models/event";
import { combineDateAndTime, getNow } from "./DateFn";

type EventWithVenueFields = EventData & {
  venue_name?: string;
  city?: string;
  line1?: string;
  /** List API may return venue as a plain string (e.g. "Ahmedabad"). */
  venue?: venueType | string;
};

function formatVenueObject(venue: venueType): string | null {
  const name = typeof venue.name === "string" ? venue.name.trim() : "";
  const city = typeof venue.city === "string" ? venue.city.trim() : "";
  const state = typeof venue.state === "string" ? venue.state.trim() : "";
  const line1 = typeof venue.line1 === "string" ? venue.line1.trim() : "";

  const parts = [name, city, state && state !== city ? state : null].filter(
    Boolean,
  ) as string[];

  if (parts.length > 0) {
    return parts.join(", ");
  }

  if (line1) {
    return city ? `${line1}, ${city}` : line1;
  }

  return null;
}

/** Normalize venue from API (string city/name or full venue object). */
export function resolveVenueText(venue: unknown): string | null {
  if (venue == null) return null;

  if (typeof venue === "string") {
    const text = venue.trim();
    return text.length > 0 ? text : null;
  }

  if (typeof venue === "object" && !Array.isArray(venue)) {
    return formatVenueObject(venue as venueType);
  }

  return null;
}

/** Human-readable venue line for event cards (always returns a string). */
export function getEventVenueLabel(event: EventData): string {
  const extended = event as EventWithVenueFields;

  if (extended.event_type === "online") {
    return "Online event";
  }

  const fromVenue = resolveVenueText(extended.venue);
  if (fromVenue) {
    return fromVenue;
  }

  const fromAlternate = resolveVenueText(extended.alternate_venue);
  if (fromAlternate) {
    return fromAlternate;
  }

  const flatParts = [extended.venue_name, extended.city, extended.line1].filter(
    (part) => typeof part === "string" && part.trim().length > 0,
  ) as string[];

  if (flatParts.length > 0) {
    return flatParts.join(", ");
  }

  if (event.yet_to_be_announced) {
    return "Venue yet to be announced";
  }

  if (extended.event_type === "hybrid") {
    return "Hybrid (on-ground + online)";
  }

  return "Venue not specified";
}

/** Past, completed, or archived events cannot use BIB / corral assignment. */
export function isEventPastOrArchived(event?: {
  status?: string;
  end_date?: string;
  end_time?: string;
}): boolean {
  if (!event) return false;

  if (event.status === "archived" || event.status === "completed") {
    return true;
  }

  const end = combineDateAndTime(event.end_date, event.end_time);
  if (end?.isValid() && end.isBefore(getNow())) {
    return true;
  }

  return false;
}

export type EventCardFooterTone =
  | "draft"
  | "pending"
  | "ready"
  | "live"
  | "rejected"
  | "scheduled";

export type EventCardFooterMessage = {
  text: string;
  tone: EventCardFooterTone;
};

/** Status hint shown on My Events cards (draft → published lifecycle). */
export function getEventCardFooterMessage(
  event: Pick<EventData, "status" | "admin_remark">,
): EventCardFooterMessage | null {
  if (event.status === "completed" || event.status === "archived") {
    return null;
  }

  switch (event.status) {
    case "draft":
      return {
        text: "Complete and submit for review",
        tone: "draft",
      };
    case "requested":
      return {
        text: "Waiting for admin review",
        tone: "pending",
      };
    case "approved":
      return {
        text: "Ready to publish",
        tone: "ready",
      };
    case "published":
      return {
        text: "Event is now live!",
        tone: "live",
      };
    case "rejected":
      return {
        text: "Event rejected by admin, Please  resubmit",
        tone: "rejected",
      };
    case "scheduled":
      return {
        text: "Scheduled to go live!",
        tone: "scheduled",
      };
    default:
      return null;
  }
}
